import type { MonitoringBucket, MonitoringRange } from '@/api/runners';

export const MONITORING_RANGES: readonly MonitoringRange[] = ['1h', '24h', '7d', '30d', '90d'];
export const DEFAULT_MONITORING_RANGE: MonitoringRange = '24h';

export const RANGE_LABELS: Record<MonitoringRange, string> = {
  '1h': '1h',
  '24h': '24h',
  '7d': '7d',
  '30d': '1m',
  '90d': '3m',
};

/** Rolling windows, not calendar months. */
export const RANGE_DESCRIPTIONS: Record<MonitoringRange, string> = {
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};
export const STALE_AFTER_MS = 3 * 60 * 1000;

const MIB = 1024 * 1024;
const GIB = 1024 * MIB;

export type ByteUnit = 'MB' | 'GB';

export interface Axis {
  /** Upper bound of the y-axis, in display units. */
  max: number;
  /** Display unit label; '%' for CPU, empty for counts. */
  unit: string;
  /** Converts a raw value to display units. */
  divisor: number;
}

export function isMonitoringRange(value: unknown): value is MonitoringRange {
  return typeof value === 'string' && (MONITORING_RANGES as readonly string[]).includes(value);
}

/** A backend without the field, or unknown future IDs, must not reveal the selector. */
export function availableRangesOf(value: unknown): MonitoringRange[] {
  if (!Array.isArray(value)) return [];
  return MONITORING_RANGES.filter((range) => value.includes(range));
}

export function normalizeRange(
  selected: MonitoringRange,
  available: readonly MonitoringRange[]
): MonitoringRange {
  if (available.length === 0 || available.includes(selected)) return selected;
  if (available.includes(DEFAULT_MONITORING_RANGE)) return DEFAULT_MONITORING_RANGE;
  return available[available.length - 1];
}

export function byteUnitFor(capacityBytes: number): ByteUnit {
  return capacityBytes < GIB ? 'MB' : 'GB';
}

export function percentAxis(): Axis {
  return { max: 100, unit: '%', divisor: 1 };
}

export function countAxis(max: number): Axis {
  return { max: Math.max(1, Math.ceil(max)), unit: '', divisor: 1 };
}

export function byteAxis(capacityBytes: number | null | undefined): Axis {
  const capacity = capacityBytes != null && capacityBytes > 0 ? capacityBytes : 0;
  const unit = byteUnitFor(capacity);
  const divisor = unit === 'MB' ? MIB : GIB;
  return { max: Math.max(1, Math.ceil(capacity / divisor)), unit, divisor };
}

export function toDisplay(value: number | null | undefined, axis: Axis): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return Math.round(value / axis.divisor);
}

export function formatValue(value: number | null | undefined, axis: Axis): string {
  const display = toDisplay(value, axis);
  return display == null ? '—' : withUnit(display, axis);
}

export function withUnit(display: number, axis: Axis): string {
  if (axis.unit === '%') return `${display}%`;
  return axis.unit ? `${display} ${axis.unit}` : String(display);
}

/** "used / total unit" for byte axes, e.g. "3 / 8 GB". */
export function formatUsage(
  used: number | null | undefined,
  total: number | null | undefined,
  axis: Axis
): string {
  const usedDisplay = toDisplay(used, axis);
  if (usedDisplay == null) return '—';
  const totalDisplay = toDisplay(total, axis);
  return totalDisplay == null
    ? `${usedDisplay} ${axis.unit}`
    : `${usedDisplay} / ${totalDisplay} ${axis.unit}`;
}

export function valueTicks(max: number): number[] {
  return [...new Set([0, Math.round(max / 2), max])];
}

const TICK_STEP_MINUTES: Partial<Record<MonitoringRange, number>> = {
  '1h': 15,
  '24h': 6 * 60,
};

// Sparse date ticks keep long-range labels legible on narrow cards.
const TICK_DAYS: Partial<Record<MonitoringRange, readonly number[]>> = {
  '30d': [1, 11, 21],
  '90d': [1],
};

/** X tick positions aligned to round local times (quarter hours, 6-hour marks, midnights). */
export function timeTicks(from: number, to: number, range: MonitoringRange): number[] {
  const step = TICK_STEP_MINUTES[range];
  const cursor = new Date(from);
  cursor.setSeconds(0, 0);
  if (step === undefined || step >= 60) cursor.setMinutes(0);
  if (step === undefined) cursor.setHours(0);
  const days = TICK_DAYS[range];
  const ticks: number[] = [];
  while (cursor.getTime() <= to) {
    if (cursor.getTime() >= from) {
      if (step === undefined) {
        if (!days || days.includes(cursor.getDate())) ticks.push(cursor.getTime());
      } else if ((cursor.getHours() * 60 + cursor.getMinutes()) % step === 0) {
        ticks.push(cursor.getTime());
      }
    }
    // Advancing local fields keeps ticks on round local times across DST changes.
    if (step === undefined) cursor.setDate(cursor.getDate() + 1);
    else if (step >= 60) cursor.setHours(cursor.getHours() + 1);
    else cursor.setMinutes(cursor.getMinutes() + 1);
  }
  return ticks;
}

export function formatTimeTick(ms: number, range: MonitoringRange): string {
  const date = new Date(ms);
  if (range === '7d') return date.toLocaleDateString([], { weekday: 'short' });
  if (range === '30d') return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  if (range === '90d') return date.toLocaleDateString([], { month: 'short' });
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function formatTooltipTime(ms: number, range: MonitoringRange): string {
  const long = range === '30d' || range === '90d';
  return new Date(ms).toLocaleString([], {
    ...(long ? { month: 'short', day: 'numeric' } : { weekday: 'short' }),
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Whole minutes since the sample, or null when it is not stale. */
export function staleMinutes(receivedAt: string, now: number): number | null {
  const received = Date.parse(receivedAt);
  if (Number.isNaN(received)) return null;
  const age = now - received;
  return age > STALE_AFTER_MS ? Math.floor(age / 60000) : null;
}

export interface SeriesPoint {
  x: number;
  y: number | null;
}

/**
 * Chart points in display units. A null point is inserted wherever a bucket is
 * missing or the generation changes, so the line breaks instead of bridging.
 */
export function buildSeries(
  history: MonitoringBucket[],
  bucketSeconds: number,
  pick: (bucket: MonitoringBucket) => number | null | undefined,
  axis: Axis
): SeriesPoint[] {
  const bucketMs = bucketSeconds * 1000;
  const sorted = history
    .map((bucket) => ({ bucket, at: Date.parse(bucket.at) }))
    .filter((entry) => !Number.isNaN(entry.at))
    .sort((a, b) => a.at - b.at || a.bucket.generation - b.bucket.generation);

  const points: SeriesPoint[] = [];
  let previous: { at: number; generation: number } | null = null;
  for (const { bucket, at } of sorted) {
    if (previous && (at - previous.at > bucketMs || bucket.generation !== previous.generation)) {
      points.push({ x: (previous.at + at) / 2, y: null });
    }
    const value = pick(bucket);
    // Unrounded so the line stays smooth; ticks and tooltips round to whole numbers.
    points.push({
      x: at,
      y: value != null && Number.isFinite(value) ? value / axis.divisor : null,
    });
    previous = { at, generation: bucket.generation };
  }
  return points;
}

/** Failure statuses only; a working export shows no status line. */
const exportStatusTexts: Record<string, string> = {
  unreachable: 'Endpoint unreachable',
  tls: 'TLS failed, for example an untrusted certificate',
  auth: 'Credentials rejected (HTTP 401/403)',
  rejected: 'Request rejected, check the endpoint URL',
  server: 'Endpoint server error (HTTP 5xx)',
  timeout: 'Endpoint timed out',
  internal: 'The collector could not send from the runner',
};

export function exportStatusText(status: string): string {
  return exportStatusTexts[status] ?? 'Unknown status';
}

export function reportedAgo(reportedAt: string, now: number): string {
  const reported = Date.parse(reportedAt);
  if (Number.isNaN(reported)) return '';
  const minutes = Math.floor((now - reported) / 60000);
  return minutes < 1 ? 'reported just now' : `reported ${minutes} min ago`;
}
